import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { searchAll } from "@/utils/catalog";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const result = useMemo(() => searchAll(keyword), [keyword]);
  const hasResult = result.stories.length + result.casts.length + result.clips.length > 0;

  useEffect(() => {
    if (!open) setKeyword("");
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-foreground-950/40 px-4 pt-24">
      <div className="w-full max-w-2xl animate-fade-up">
        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              autoFocus
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧情、演员或精彩片段…"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-xs text-foreground-500 transition hover:bg-background-100"
            >
              ESC
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {!keyword.trim() && (
              <p className="px-3 py-8 text-center text-sm text-foreground-400">
                输入关键词，快速查找剧情、演员与片段
              </p>
            )}
            {keyword.trim() && !hasResult && (
              <p className="px-3 py-8 text-center text-sm text-foreground-400">
                未找到“{keyword}”相关内容，换个关键词试试
              </p>
            )}
            {result.stories.length > 0 && (
              <div className="mb-2">
                <p className="px-3 py-2 text-xs text-foreground-400">剧情介绍</p>
                {result.stories.map((story) => (
                  <Link
                    key={story.id}
                    to={`/detail/story/${story.id}`}
                    onClick={onClose}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-primary-50"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-book-2-line"></i>
                    </span>
                    <span className="flex-1 truncate text-sm text-foreground-800">{story.title}</span>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </Link>
                ))}
              </div>
            )}
            {result.casts.length > 0 && (
              <div className="mb-2">
                <p className="px-3 py-2 text-xs text-foreground-400">演员名单</p>
                {result.casts.map((cast) => (
                  <Link
                    key={cast.id}
                    to={`/detail/cast/${cast.id}`}
                    onClick={onClose}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-primary-50"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-user-star-line"></i>
                    </span>
                    <span className="flex-1 truncate text-sm text-foreground-800">
                      {cast.name} · 饰 {cast.role}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </Link>
                ))}
              </div>
            )}
            {result.clips.length > 0 && (
              <div>
                <p className="px-3 py-2 text-xs text-foreground-400">精彩片段</p>
                {result.clips.map((clip) => (
                  <Link
                    key={clip.id}
                    to={`/detail/clip/${clip.id}`}
                    onClick={onClose}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-primary-50"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-film-line"></i>
                    </span>
                    <span className="flex-1 truncate text-sm text-foreground-800">{clip.title}</span>
                    <i className="ri-arrow-right-s-line text-foreground-300"></i>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}